import './App.css';
import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import DagpengerGuide from './Dagpengerguide';

// ─── Translations ────────────────────────────────────────────────────────────
export const translations = {
  en: {
    nav: { hub: 'The Hub', mission: 'Our Mission', partners: 'Partners', contact: 'Contact', cta: 'Explore the Hub' },
    badge: 'Built for immigrants, by people who get it',
    heroTitle: ['Norway is your', 'new home.'],
    heroEm: 'Own it.',
    heroSub: 'One place. Every answer. In your language. Global Integration is the hub where newcomers to Norway find clear, verified guides to the things that matter — from registering at NAV to buying your first car.',
    heroCta1: 'Go to the Hub →',
    heroCta2: 'Our Mission',
    searchLabel: 'Ask anything about life in Norway',
    searchPlaceholder: 'e.g. "How do I apply for unemployment benefits?"',
    searchEmpty: 'No matches yet — try a different phrase.',
    searchViewAll: 'See guide →',
    flagsLabel: 'Available in 8+ languages',
    stats: [{ number: '100+', label: 'Topics covered' }, { number: '8+', label: 'Languages supported' }, { number: '0', label: 'Cost to use' }],
    hubLabel: 'The Hub',
    hubTitle: 'Everything you need, in one place.',
    hubSub: 'Step-by-step guides for real situations. No jargon. No assumptions. Just clear information so you can take action confidently.',
    tagAvailable: 'Available',
    tagSoon: 'Potential Partnership coming',
    categories: [
      { icon: '🏛️', title: 'NAV', desc: 'Benefits, job search support, unemployment — step by step.' },
      { icon: '💰', title: 'Skatteetaten', desc: 'Tax cards, tax returns, D-numbers explained simply.' },
      { icon: '🚗', title: 'Statens vegvesen', desc: 'Register a vehicle, get a Norwegian driving licence.' },
      { icon: '📋', title: 'Folkeregisteret', desc: 'Register your address, get a national ID number.' },
      { icon: '🏠', title: 'Housing', desc: 'How Finn.no works, tenant rights, what to know before signing.' },
      { icon: '🏥', title: 'Healthcare', desc: 'Register with a GP, understand the fastlege system.' },
      { icon: '🏦', title: 'Banking', desc: 'Open a Norwegian bank account as a newcomer.' },
      { icon: '🎓', title: 'Language & Education', desc: 'Norwegian courses, norskprøve, school enrollment for kids.' },
    ],
    missionLabel: 'Our Mission',
    missionTitle: 'A bridge, not a replacement.',
    missionP1: 'Norway has excellent public services. NAV, Skatteetaten, Statens vegvesen — they are well-built systems that work. But they were built for people who already know how Norway works.',
    missionP2: 'Global Integration is the bridge that gets you there. We translate complexity into clarity, so that when you walk into NAV or log on to Altinn, you already know what to expect, what to bring, and what to say.',
    missionP3: 'We do not replace any government structure. We help you use them — confidently, independently, on your own terms.',
    value1Title: 'Empowerment first', value1Desc: 'We give you the information. You make the decisions.',
    value2Title: 'Free, always', value2Desc: 'Access to information should never be a privilege.',
    value3Title: 'Verified & honest', value3Desc: 'We only publish what we can confirm. No guesswork.',
    bridgeYou: 'You', bridgeYouSub: 'New to Norway, ready to build your life',
    bridgeNorway: 'Norway', bridgeNorwaySub: 'NAV · Skatteetaten · Vegvesen · Folkeregisteret',
    partnersLabel: 'Partnerships',
    partnersTitle: 'Working with the system, not around it.',
    partnersSub: 'We are actively building partnerships with Norwegian public institutions so our guides are verified, up-to-date, and directly linked to the right place. No more dead ends.',
    partnerStatus: 'In pipeline',
    partnerNote: 'Are you from one of these institutions and want to collaborate?',
    partnerNoteCta: 'Get in touch →',
    footerTagline: 'Your bridge to life in Norway.\nClear guides. Verified info. Free, always.',
    teamLabel: 'The Team',
    role: 'Co-founder',
    footerNote: 'A bridge to Norway — not a replacement for it.',
  },
  no: {
    nav: { hub: 'Huben', mission: 'Vårt oppdrag', partners: 'Partnere', contact: 'Kontakt', cta: 'Utforsk huben' },
    badge: 'Bygget for innvandrere, av folk som forstår det',
    heroTitle: ['Norge er ditt', 'nye hjem.'],
    heroEm: 'Ta det i bruk.',
    heroSub: 'Ett sted. Alle svar. På ditt språk. Global Integration er huben der nykommere til Norge finner klare, verifiserte guider til det som betyr noe — fra å registrere seg hos NAV til å kjøpe din første bil.',
    heroCta1: 'Gå til huben →',
    heroCta2: 'Vårt oppdrag',
    searchLabel: 'Spør om hva som helst om livet i Norge',
    searchPlaceholder: 'f.eks. "Hvordan søker jeg om dagpenger?"',
    searchEmpty: 'Ingen treff ennå — prøv en annen formulering.',
    searchViewAll: 'Se guide →',
    flagsLabel: 'Tilgjengelig på 8+ språk',
    stats: [{ number: '100+', label: 'Emner dekket' }, { number: '8+', label: 'Språk støttet' }, { number: '0', label: 'Kostnad å bruke' }],
    hubLabel: 'Huben',
    hubTitle: 'Alt du trenger, på ett sted.',
    hubSub: 'Trinn-for-trinn-guider for virkelige situasjoner. Ingen sjargong. Ingen antagelser. Bare klar informasjon slik at du kan handle trygt.',
    tagAvailable: 'Tilgjengelig',
    tagSoon: 'Potensielt partnerskap kommer',
    categories: [
      { icon: '🏛️', title: 'NAV', desc: 'Ytelser, jobbsøkerstøtte, arbeidsledighet — steg for steg.' },
      { icon: '💰', title: 'Skatteetaten', desc: 'Skattekort, selvangivelse, D-nummer forklart enkelt.' },
      { icon: '🚗', title: 'Statens vegvesen', desc: 'Registrer kjøretøy, få norsk førerkort.' },
      { icon: '📋', title: 'Folkeregisteret', desc: 'Registrer adressen din, få personnummer.' },
      { icon: '🏠', title: 'Bolig', desc: 'Slik fungerer Finn.no, leietakerrettigheter, hva du bør vite før du signerer.' },
      { icon: '🏥', title: 'Helsevesen', desc: 'Registrer deg hos fastlege, forstå fastlegeordningen.' },
      { icon: '🏦', title: 'Bank', desc: 'Åpne en norsk bankkonto som nykommer.' },
      { icon: '🎓', title: 'Språk og utdanning', desc: 'Norskkurs, norskprøve, skoleinnmelding for barn.' },
    ],
    missionLabel: 'Vårt oppdrag',
    missionTitle: 'En bro, ikke en erstatning.',
    missionP1: 'Norge har utmerkede offentlige tjenester. NAV, Skatteetaten, Statens vegvesen — de er godt bygde systemer som fungerer. Men de ble bygget for folk som allerede kjenner til hvordan Norge fungerer.',
    missionP2: 'Global Integration er broen som tar deg dit. Vi oversetter kompleksitet til klarhet, slik at når du går inn på NAV eller logger på Altinn, vet du allerede hva du kan forvente, hva du skal ta med og hva du skal si.',
    missionP3: 'Vi erstatter ingen offentlig struktur. Vi hjelper deg å bruke dem — trygt, selvstendig, på dine egne premisser.',
    value1Title: 'Myndiggjøring først', value1Desc: 'Vi gir deg informasjonen. Du tar beslutningene.',
    value2Title: 'Gratis, alltid', value2Desc: 'Tilgang til informasjon skal aldri være et privilegium.',
    value3Title: 'Verifisert og ærlig', value3Desc: 'Vi publiserer bare det vi kan bekrefte. Ingen gjetning.',
    bridgeYou: 'Deg', bridgeYouSub: 'Ny i Norge, klar til å bygge livet ditt',
    bridgeNorway: 'Norge', bridgeNorwaySub: 'NAV · Skatteetaten · Vegvesen · Folkeregisteret',
    partnersLabel: 'Partnerskap',
    partnersTitle: 'Jobber med systemet, ikke rundt det.',
    partnersSub: 'Vi bygger aktivt partnerskap med norske offentlige institusjoner slik at guidene våre er verifiserte, oppdaterte og direkte koblet til rett sted. Ingen flere blindveier.',
    partnerStatus: 'I pipeline',
    partnerNote: 'Er du fra en av disse institusjonene og ønsker å samarbeide?',
    partnerNoteCta: 'Ta kontakt →',
    footerTagline: 'Din bro til livet i Norge.\nKlare guider. Verifisert info. Gratis, alltid.',
    teamLabel: 'Teamet',
    role: 'Medgründer',
    footerNote: 'En bro til Norge — ikke en erstatning for den.',
  },
  ar: {
    nav: { hub: 'المركز', mission: 'مهمتنا', partners: 'الشركاء', contact: 'اتصل بنا', cta: 'استكشف المركز' },
    badge: 'مبني للمهاجرين، من قِبل أناس يفهمون',
    heroTitle: ['النرويج هي', 'وطنك الجديد.'],
    heroEm: 'امتلكه.',
    heroSub: 'مكان واحد. كل إجابة. بلغتك. Global Integration هو المركز الذي يجد فيه القادمون الجدد إلى النرويج أدلة واضحة وموثّقة للأمور المهمة — من التسجيل في NAV إلى شراء سيارتك الأولى.',
    heroCta1: 'اذهب إلى المركز →',
    heroCta2: 'مهمتنا',
    searchLabel: 'اسأل أي شيء عن الحياة في النرويج',
    searchPlaceholder: 'مثلاً: "كيف أتقدم بطلب إعانة البطالة؟"',
    searchEmpty: 'لا توجد نتائج — جرّب صياغة مختلفة.',
    searchViewAll: 'عرض الدليل →',
    flagsLabel: 'متاح بأكثر من 8 لغات',
    stats: [{ number: '+100', label: 'موضوع مشمول' }, { number: '+8', label: 'لغات مدعومة' }, { number: '0', label: 'تكلفة الاستخدام' }],
    hubLabel: 'المركز',
    hubTitle: 'كل ما تحتاجه، في مكان واحد.',
    hubSub: 'أدلة خطوة بخطوة لمواقف حقيقية. لا مصطلحات معقدة. لا افتراضات. فقط معلومات واضحة حتى تتصرف بثقة.',
    tagAvailable: 'متاح',
    tagSoon: 'شراكة محتملة قادمة',
    categories: [
      { icon: '🏛️', title: 'NAV', desc: 'المزايا، دعم البحث عن عمل، البطالة — خطوة بخطوة.' },
      { icon: '💰', title: 'Skatteetaten', desc: 'بطاقات الضرائب، الإقرارات الضريبية، أرقام D موضحة ببساطة.' },
      { icon: '🚗', title: 'Statens vegvesen', desc: 'تسجيل مركبة، الحصول على رخصة قيادة نرويجية.' },
      { icon: '📋', title: 'Folkeregisteret', desc: 'تسجيل عنوانك، الحصول على رقم هوية وطني.' },
      { icon: '🏠', title: 'السكن', desc: 'كيف يعمل Finn.no، حقوق المستأجر، ما يجب معرفته قبل التوقيع.' },
      { icon: '🏥', title: 'الرعاية الصحية', desc: 'التسجيل لدى طبيب عام، فهم نظام fastlege.' },
      { icon: '🏦', title: 'البنوك', desc: 'فتح حساب مصرفي نرويجي كقادم جديد.' },
      { icon: '🎓', title: 'اللغة والتعليم', desc: 'دورات النرويجية، norskprøve، تسجيل الأطفال في المدارس.' },
    ],
    missionLabel: 'مهمتنا',
    missionTitle: 'جسر، لا بديل.',
    missionP1: 'تمتلك النرويج خدمات عامة ممتازة. NAV وSkatteetaten وStatens vegvesen — أنظمة مبنية بشكل جيد وتعمل. لكنها بُنيت لأشخاص يعرفون بالفعل كيف تعمل النرويج.',
    missionP2: 'Global Integration هو الجسر الذي يوصلك إلى هناك. نترجم التعقيد إلى وضوح، حتى حين تدخل إلى NAV أو تسجّل الدخول إلى Altinn، تعرف بالفعل ما تتوقعه وماذا تحمل وما تقوله.',
    missionP3: 'لا نحلّ محل أي هيكل حكومي. نساعدك على استخدامه — بثقة، باستقلالية، بشروطك الخاصة.',
    value1Title: 'التمكين أولاً', value1Desc: 'نعطيك المعلومات. أنت تتخذ القرارات.',
    value2Title: 'مجاني دائماً', value2Desc: 'الوصول إلى المعلومات لا يجب أن يكون امتيازاً.',
    value3Title: 'موثّق وصادق', value3Desc: 'ننشر فقط ما يمكننا تأكيده. لا تخمين.',
    bridgeYou: 'أنت', bridgeYouSub: 'جديد في النرويج، مستعد لبناء حياتك',
    bridgeNorway: 'النرويج', bridgeNorwaySub: 'NAV · Skatteetaten · Vegvesen · Folkeregisteret',
    partnersLabel: 'الشراكات',
    partnersTitle: 'نعمل مع النظام، لا حوله.',
    partnersSub: 'نبني بنشاط شراكات مع المؤسسات العامة النرويجية حتى تكون أدلتنا موثّقة ومحدّثة ومرتبطة مباشرة بالمكان الصحيح. لا طرق مسدودة بعد الآن.',
    partnerStatus: 'في الأنبوب',
    partnerNote: 'هل أنت من إحدى هذه المؤسسات وتريد التعاون؟',
    partnerNoteCta: 'تواصل معنا →',
    footerTagline: 'جسرك إلى الحياة في النرويج.\nأدلة واضحة. معلومات موثّقة. مجاني دائماً.',
    teamLabel: 'الفريق',
    role: 'مؤسس مشارك',
    footerNote: 'جسر إلى النرويج — لا بديل عنها.',
  },
  so: {
    nav: { hub: 'Xarunta', mission: 'Hawsheenna', partners: 'Wada-shaqeeyayaasha', contact: 'Nala soo xiriir', cta: 'Baadh Xarunta' },
    badge: 'U dhisay socdaalayaasha, dadka fahma',
    heroTitle: ['Norwaygu waa', 'gurigaaga cusub.'],
    heroEm: 'Ku leh.',
    heroSub: 'Meel kasta. Jawaab walba. Luqaddaada. Global Integration waa xarunta halka cusbitaalada Norway ay ka helaan hagaha cad, la xaqiijiyay ee arrimaha muhiimka ah — laga bilaabo NAV ilaa gaari-gaadiid iibsashada.',
    heroCta1: 'Aad Xarunta →',
    heroCta2: 'Hawsheenna',
    searchLabel: 'Wax kasta ka weydii noloshada Norway',
    searchPlaceholder: 'Tusaale: "Sideen u codsan karaa lacagta shaqo la\'aanta?"',
    searchEmpty: 'Ma jirto natiijo — isku day odhaah kale.',
    searchViewAll: 'Arag hagaha →',
    flagsLabel: 'Ku heli kartaa 8+ luqadood',
    stats: [{ number: '100+', label: 'Mawduucyo la daboolay' }, { number: '8+', label: 'Luqado la taageeray' }, { number: '0', label: 'Kharash isticmaalka' }],
    hubLabel: 'Xarunta',
    hubTitle: 'Waxba aad u baahan tahay, meel kasta.',
    hubSub: 'Hago tallaabo-tallaabo oo xaaladaha dhabta ah. Ma jiraan erayo adag. Ma jiraan farsamooyin. Macluumaad cad oo kuu oggolaanaya inaad si kalsooni leh u tallaabsato.',
    tagAvailable: 'La heli karo',
    tagSoon: 'Wada-shaqayn suurtagal ah ayaa socota',
    categories: [
      { icon: '🏛️', title: 'NAV', desc: "Faa'iidooyinka, taageerada raadinta shaqada, shaqo la'aanta — tallaabo tallaabo." },
      { icon: '💰', title: 'Skatteetaten', desc: 'Kaardhyada canshuurta, soo celinta canshuurta, D-numbers si fudud loo sharaxay.' },
      { icon: '🚗', title: 'Statens vegvesen', desc: 'Diiwaangeli gaadiid, hel shuhadda wadista Norweejianka.' },
      { icon: '📋', title: 'Folkeregisteret', desc: 'Diiwaangeli ciwaankaaga, hel lambarka aqoonsiga qaranka.' },
      { icon: '🏠', title: 'Guryeynta', desc: 'Sida Finn.no u shaqeeyo, xuquuqda kirayste, waxa la garanaayo ka hor saxiixida.' },
      { icon: '🏥', title: 'Caafimaadka', desc: 'Iska diiwaangeli dhakhtarka guriga, faham nidaamka fastlege.' },
      { icon: '🏦', title: 'Bangiga', desc: 'Fur akoon bangi Norweejiyaan ah sida cusub.' },
      { icon: '🎓', title: 'Luqadda & Waxbarashada', desc: 'Koorsooyinka Norweejianka, norskprøve, diiwaangelinta iskuulka caruurta.' },
    ],
    missionLabel: 'Hawsheenna',
    missionTitle: 'Buundada, ma beddelka.',
    missionP1: 'Norway waxay leedahay adeegyada dadweynaha ee heer sare ah. NAV, Skatteetaten, Statens vegvesen — nidaamyo si fiican loo dhisay oo shaqeeya. Laakiin waxaa loo dhisay dadka horeba u garanaya sida Norway u shaqeyso.',
    missionP2: 'Global Integration waa buundada kuu gaara halkaas. Waxaan u turjumnaa adag si cad, si markaad gashid NAV ama galid Altinn, horeba u garaneyso waxa aad filayso, waxa aad soo qaadeyso, iyo waxa aad odhaneyso.',
    missionP3: 'Kuma beddelno wax nidaam dowladeed ah. Waxaan kaa cawinaa inaad isticmaasho — kalsooni leh, madax-bannaan, shuruudahaaga.',
    value1Title: 'Awood-siinta horeba', value1Desc: 'Waxaan kuu siinaa macluumaadka. Adiga ayaa go\'aanka gaara.',
    value2Title: 'Bilaash, had iyo jeer', value2Desc: 'Helitaanka macluumaadka ma noqon karo mudnaanta.',
    value3Title: 'La xaqiijiyay & daacadnimada', value3Desc: 'Waxaan mabaahinnaa waxa aan xaqiijin karno. Wax tixraac ah kuma jiro.',
    bridgeYou: 'Adiga', bridgeYouSub: 'Cusub Norway, diyaar u ah in aad dhisto noloshaada',
    bridgeNorway: 'Norway', bridgeNorwaySub: 'NAV · Skatteetaten · Vegvesen · Folkeregisteret',
    partnersLabel: 'Wada-shaqaynta',
    partnersTitle: 'La shaqeynaynaa nidaamka, ma hareeraheeda.',
    partnersSub: "Si firfircoon ayaan u dhisaynaa wada-shaqaynta hay'adaha dadweynaha Norweejianka si hagahayagu u noqdaan kuwo la xaqiijiyay, kuwo cusbooneysiiyay, oo si toos ah ugu xidnaan meesha saxda ah.",
    partnerStatus: 'Wadanka',
    partnerNote: "Ma ka tirsan tahay mid ka mid ah hay'adahan oo rabtid inaad wada-shaqeyso?",
    partnerNoteCta: 'Nala soo xiriir →',
    footerTagline: 'Buundadaada nolosha Norway.\nHago cad. Macluumaad la xaqiijiyay. Bilaash, had iyo jeer.',
    teamLabel: 'Kooxda',
    role: 'Aasaasaha wadajirka ah',
    footerNote: 'Buundo Norway — ma beddel.',
  },
  pl: {
    nav: { hub: 'Hub', mission: 'Nasza misja', partners: 'Partnerzy', contact: 'Kontakt', cta: 'Odkryj Hub' },
    badge: 'Stworzony dla imigrantów, przez ludzi, którzy rozumieją',
    heroTitle: ['Norwegia to twój', 'nowy dom.'],
    heroEm: 'Opanuj go.',
    heroSub: 'Jedno miejsce. Każda odpowiedź. W twoim języku. Global Integration to centrum, gdzie nowo przybyłe osoby do Norwegii znajdą jasne, zweryfikowane przewodniki dotyczące ważnych spraw — od rejestracji w NAV po zakup pierwszego samochodu.',
    heroCta1: 'Przejdź do Hubu →',
    heroCta2: 'Nasza misja',
    searchLabel: 'Zapytaj o cokolwiek związanego z życiem w Norwegii',
    searchPlaceholder: 'np. „Jak złożyć wniosek o zasiłek dla bezrobotnych?"',
    searchEmpty: 'Brak wyników — spróbuj innego sformułowania.',
    searchViewAll: 'Zobacz przewodnik →',
    flagsLabel: 'Dostępne w 8+ językach',
    stats: [{ number: '100+', label: 'Poruszonych tematów' }, { number: '8+', label: 'Obsługiwanych języków' }, { number: '0', label: 'Koszt użytkowania' }],
    hubLabel: 'Hub',
    hubTitle: 'Wszystko czego potrzebujesz, w jednym miejscu.',
    hubSub: 'Przewodniki krok po kroku dla realnych sytuacji. Bez żargonu. Bez założeń. Tylko jasne informacje, abyś mógł działać pewnie.',
    tagAvailable: 'Dostępne',
    tagSoon: 'Potencjalne partnerstwo w drodze',
    categories: [
      { icon: '🏛️', title: 'NAV', desc: 'Zasiłki, wsparcie w poszukiwaniu pracy, bezrobocie — krok po kroku.' },
      { icon: '💰', title: 'Skatteetaten', desc: 'Karty podatkowe, zeznania podatkowe, numery D wyjaśnione prosto.' },
      { icon: '🚗', title: 'Statens vegvesen', desc: 'Zarejestruj pojazd, uzyskaj norweskie prawo jazdy.' },
      { icon: '📋', title: 'Folkeregisteret', desc: 'Zarejestruj adres, uzyskaj krajowy numer identyfikacyjny.' },
      { icon: '🏠', title: 'Mieszkanie', desc: 'Jak działa Finn.no, prawa najemcy, co wiedzieć przed podpisaniem.' },
      { icon: '🏥', title: 'Opieka zdrowotna', desc: 'Zarejestruj się u lekarza pierwszego kontaktu, zrozum system fastlege.' },
      { icon: '🏦', title: 'Bankowość', desc: 'Otwórz norweski rachunek bankowy jako nowa osoba.' },
      { icon: '🎓', title: 'Język i edukacja', desc: 'Kursy norweskiego, norskprøve, zapisy dzieci do szkoły.' },
    ],
    missionLabel: 'Nasza misja',
    missionTitle: 'Most, nie zastępstwo.',
    missionP1: 'Norwegia ma doskonałe usługi publiczne. NAV, Skatteetaten, Statens vegvesen — dobrze zbudowane systemy, które działają. Ale zostały zbudowane dla ludzi, którzy już wiedzą, jak działa Norwegia.',
    missionP2: 'Global Integration to most, który tam cię zaprowadzi. Tłumaczymy złożoność na jasność, aby kiedy wejdziesz do NAV lub zalogujesz się na Altinn, już wiedział czego się spodziewać, co zabrać i co powiedzieć.',
    missionP3: 'Nie zastępujemy żadnej struktury rządowej. Pomagamy ci jej używać — pewnie, niezależnie, na własnych warunkach.',
    value1Title: 'Najpierw upodmiotowienie', value1Desc: 'Dajemy ci informacje. Ty podejmujesz decyzje.',
    value2Title: 'Bezpłatne, zawsze', value2Desc: 'Dostęp do informacji nigdy nie powinien być przywilejem.',
    value3Title: 'Zweryfikowane i uczciwe', value3Desc: 'Publikujemy tylko to, co możemy potwierdzić. Żadnych domysłów.',
    bridgeYou: 'Ty', bridgeYouSub: 'Nowy w Norwegii, gotowy do budowania życia',
    bridgeNorway: 'Norwegia', bridgeNorwaySub: 'NAV · Skatteetaten · Vegvesen · Folkeregisteret',
    partnersLabel: 'Partnerstwa',
    partnersTitle: 'Pracujemy z systemem, nie wokół niego.',
    partnersSub: 'Aktywnie budujemy partnerstwa z norweskimi instytucjami publicznymi, aby nasze przewodniki były zweryfikowane, aktualne i bezpośrednio połączone z właściwym miejscem. Koniec ze ślepymi zaułkami.',
    partnerStatus: 'W planach',
    partnerNote: 'Czy jesteś z jednej z tych instytucji i chcesz współpracować?',
    partnerNoteCta: 'Skontaktuj się →',
    footerTagline: 'Twój most do życia w Norwegii.\nJasne przewodniki. Zweryfikowane info. Bezpłatne, zawsze.',
    teamLabel: 'Zespół',
    role: 'Współzałożyciel',
    footerNote: 'Most do Norwegii — nie zastępstwo.',
  },
  ur: {
    nav: { hub: 'ہب', mission: 'ہمارا مشن', partners: 'شراکت دار', contact: 'رابطہ', cta: 'ہب دریافت کریں' },
    badge: 'تارکین وطن کے لیے بنایا گیا، ان لوگوں کی طرف سے جو سمجھتے ہیں',
    heroTitle: ['ناروے آپ کا', 'نیا گھر ہے۔'],
    heroEm: 'اسے اپنائیں۔',
    heroSub: 'ایک جگہ۔ ہر جواب۔ آپ کی زبان میں۔ Global Integration وہ مرکز ہے جہاں ناروے کے نئے آنے والے واضح، تصدیق شدہ رہنمائی حاصل کرتے ہیں — NAV میں رجسٹریشن سے لے کر اپنی پہلی گاڑی خریدنے تک۔',
    heroCta1: 'ہب پر جائیں →',
    heroCta2: 'ہمارا مشن',
    searchLabel: 'ناروے میں زندگی کے بارے میں کچھ بھی پوچھیں',
    searchPlaceholder: 'مثلاً "میں بیروزگاری الاؤنس کے لیے کیسے درخواست دوں؟"',
    searchEmpty: 'کوئی نتائج نہیں — مختلف الفاظ آزمائیں۔',
    searchViewAll: 'رہنمائی دیکھیں →',
    flagsLabel: '8+ زبانوں میں دستیاب',
    stats: [{ number: '+100', label: 'موضوعات شامل' }, { number: '+8', label: 'زبانیں سپورٹ شدہ' }, { number: '0', label: 'استعمال کی لاگت' }],
    hubLabel: 'ہب',
    hubTitle: 'آپ کو جو کچھ چاہیے، ایک جگہ۔',
    hubSub: 'حقیقی حالات کے لیے مرحلہ وار رہنمائی۔ کوئی اصطلاح نہیں۔ کوئی مفروضات نہیں۔ صرف واضح معلومات تاکہ آپ اعتماد سے قدم اٹھا سکیں۔',
    tagAvailable: 'دستیاب',
    tagSoon: 'ممکنہ شراکت آ رہی ہے',
    categories: [
      { icon: '🏛️', title: 'NAV', desc: 'فوائد، نوکری کی تلاش میں مدد، بے روزگاری — مرحلہ وار۔' },
      { icon: '💰', title: 'Skatteetaten', desc: 'ٹیکس کارڈ، ٹیکس ریٹرن، D-نمبر آسانی سے سمجھائے۔' },
      { icon: '🚗', title: 'Statens vegvesen', desc: 'گاڑی رجسٹر کریں، ناروے کا ڈرائیونگ لائسنس حاصل کریں۔' },
      { icon: '📋', title: 'Folkeregisteret', desc: 'اپنا پتہ رجسٹر کریں، قومی شناختی نمبر حاصل کریں۔' },
      { icon: '🏠', title: 'رہائش', desc: 'Finn.no کیسے کام کرتا ہے، کرایہ دار کے حقوق، دستخط کرنے سے پہلے کیا جاننا چاہیے۔' },
      { icon: '🏥', title: 'صحت کی دیکھ بھال', desc: 'GP کے ساتھ رجسٹر کریں، fastlege نظام سمجھیں۔' },
      { icon: '🏦', title: 'بینکاری', desc: 'نئے آنے والے کے طور پر ناروے کا بینک اکاؤنٹ کھولیں۔' },
      { icon: '🎓', title: 'زبان اور تعلیم', desc: 'ناروے کے کورسز، norskprøve، بچوں کا اسکول میں داخلہ۔' },
    ],
    missionLabel: 'ہمارا مشن',
    missionTitle: 'ایک پل، متبادل نہیں۔',
    missionP1: 'ناروے میں بہترین عوامی خدمات ہیں۔ NAV، Skatteetaten، Statens vegvesen — یہ اچھی طرح بنائے گئے نظام ہیں جو کام کرتے ہیں۔ لیکن یہ ان لوگوں کے لیے بنائے گئے تھے جو پہلے سے جانتے ہیں کہ ناروے کیسے کام کرتا ہے۔',
    missionP2: 'Global Integration وہ پل ہے جو آپ کو وہاں پہنچاتا ہے۔ ہم پیچیدگی کو وضاحت میں بدلتے ہیں، تاکہ جب آپ NAV میں جائیں یا Altinn پر لاگ ان کریں، تو آپ پہلے سے جانتے ہوں کہ کیا توقع رکھنی ہے، کیا لانا ہے، اور کیا کہنا ہے۔',
    missionP3: 'ہم کسی حکومتی ڈھانچے کی جگہ نہیں لیتے۔ ہم آپ کی مدد کرتے ہیں انہیں استعمال کرنے میں — اعتماد کے ساتھ، آزادانہ طور پر، اپنی شرائط پر۔',
    value1Title: 'بااختیار بنانا پہلے', value1Desc: 'ہم آپ کو معلومات دیتے ہیں۔ آپ فیصلے کرتے ہیں۔',
    value2Title: 'مفت، ہمیشہ', value2Desc: 'معلومات تک رسائی کبھی بھی استحقاق نہیں ہونا چاہیے۔',
    value3Title: 'تصدیق شدہ اور ایماندار', value3Desc: 'ہم صرف وہی شائع کرتے ہیں جو ہم تصدیق کر سکتے ہیں۔ کوئی اندازہ نہیں۔',
    bridgeYou: 'آپ', bridgeYouSub: 'ناروے میں نئے، اپنی زندگی بنانے کے لیے تیار',
    bridgeNorway: 'ناروے', bridgeNorwaySub: 'NAV · Skatteetaten · Vegvesen · Folkeregisteret',
    partnersLabel: 'شراکت داریاں',
    partnersTitle: 'نظام کے ساتھ کام کرنا، اس کے آس پاس نہیں۔',
    partnersSub: 'ہم فعال طور پر ناروے کے سرکاری اداروں کے ساتھ شراکت داریاں بنا رہے ہیں تاکہ ہماری رہنمائی تصدیق شدہ، تازہ ترین اور صحیح جگہ سے براہ راست منسلک ہو۔ کوئی مردہ سرے نہیں۔',
    partnerStatus: 'پائپ لائن میں',
    partnerNote: 'کیا آپ ان اداروں میں سے کسی سے ہیں اور تعاون کرنا چاہتے ہیں؟',
    partnerNoteCta: 'رابطہ کریں →',
    footerTagline: 'ناروے میں زندگی کا آپ کا پل۔\nواضح رہنمائی۔ تصدیق شدہ معلومات۔ مفت، ہمیشہ۔',
    teamLabel: 'ٹیم',
    role: 'شریک بانی',
    footerNote: 'ناروے کا پل — متبادل نہیں۔',
  },
  es: {
    nav: { hub: 'El Hub', mission: 'Nuestra misión', partners: 'Socios', contact: 'Contacto', cta: 'Explorar el Hub' },
    badge: 'Hecho para inmigrantes, por gente que lo entiende',
    heroTitle: ['Noruega es tu', 'nuevo hogar.'],
    heroEm: 'Hazlo tuyo.',
    heroSub: 'Un lugar. Cada respuesta. En tu idioma. Global Integration es el hub donde los recién llegados a Noruega encuentran guías claras y verificadas sobre lo que importa — desde registrarse en NAV hasta comprar tu primer coche.',
    heroCta1: 'Ir al Hub →',
    heroCta2: 'Nuestra misión',
    searchLabel: 'Pregunta cualquier cosa sobre la vida en Noruega',
    searchPlaceholder: 'p.ej. "¿Cómo solicito el subsidio por desempleo?"',
    searchEmpty: 'Sin resultados — prueba con otras palabras.',
    searchViewAll: 'Ver guía →',
    flagsLabel: 'Disponible en más de 8 idiomas',
    stats: [{ number: '100+', label: 'Temas cubiertos' }, { number: '8+', label: 'Idiomas disponibles' }, { number: '0', label: 'Costo de uso' }],
    hubLabel: 'El Hub',
    hubTitle: 'Todo lo que necesitas, en un solo lugar.',
    hubSub: 'Guías paso a paso para situaciones reales. Sin jerga. Sin suposiciones. Solo información clara para que puedas actuar con confianza.',
    tagAvailable: 'Disponible',
    tagSoon: 'Posible asociación próximamente',
    categories: [
      { icon: '🏛️', title: 'NAV', desc: 'Prestaciones, apoyo en búsqueda de empleo, desempleo — paso a paso.' },
      { icon: '💰', title: 'Skatteetaten', desc: 'Tarjetas fiscales, declaraciones de impuestos, números D explicados claramente.' },
      { icon: '🚗', title: 'Statens vegvesen', desc: 'Registrar un vehículo, obtener un carné de conducir noruego.' },
      { icon: '📋', title: 'Folkeregisteret', desc: 'Registrar tu dirección, obtener un número de identidad nacional.' },
      { icon: '🏠', title: 'Vivienda', desc: 'Cómo funciona Finn.no, derechos del inquilino, qué saber antes de firmar.' },
      { icon: '🏥', title: 'Sanidad', desc: 'Registrarse con un médico de cabecera, entender el sistema fastlege.' },
      { icon: '🏦', title: 'Banca', desc: 'Abrir una cuenta bancaria noruega como recién llegado.' },
      { icon: '🎓', title: 'Idioma y educación', desc: 'Cursos de noruego, norskprøve, matrícula escolar para niños.' },
    ],
    missionLabel: 'Nuestra misión',
    missionTitle: 'Un puente, no un sustituto.',
    missionP1: 'Noruega tiene excelentes servicios públicos. NAV, Skatteetaten, Statens vegvesen — son sistemas bien construidos que funcionan. Pero fueron construidos para personas que ya saben cómo funciona Noruega.',
    missionP2: 'Global Integration es el puente que te lleva allí. Traducimos la complejidad en claridad, para que cuando entres en NAV o inicies sesión en Altinn, ya sepas qué esperar, qué llevar y qué decir.',
    missionP3: 'No reemplazamos ninguna estructura gubernamental. Te ayudamos a usarlas — con confianza, de forma independiente, en tus propios términos.',
    value1Title: 'Empoderamiento primero', value1Desc: 'Te damos la información. Tú tomas las decisiones.',
    value2Title: 'Gratis, siempre', value2Desc: 'El acceso a la información nunca debe ser un privilegio.',
    value3Title: 'Verificado y honesto', value3Desc: 'Solo publicamos lo que podemos confirmar. Sin suposiciones.',
    bridgeYou: 'Tú', bridgeYouSub: 'Nuevo en Noruega, listo para construir tu vida',
    bridgeNorway: 'Noruega', bridgeNorwaySub: 'NAV · Skatteetaten · Vegvesen · Folkeregisteret',
    partnersLabel: 'Alianzas',
    partnersTitle: 'Trabajando con el sistema, no a su alrededor.',
    partnersSub: 'Estamos construyendo activamente alianzas con instituciones públicas noruegas para que nuestras guías estén verificadas, actualizadas y directamente enlazadas al lugar correcto. Sin más callejones sin salida.',
    partnerStatus: 'En proceso',
    partnerNote: '¿Eres de una de estas instituciones y quieres colaborar?',
    partnerNoteCta: 'Contáctanos →',
    footerTagline: 'Tu puente a la vida en Noruega.\nGuías claras. Info verificada. Gratis, siempre.',
    teamLabel: 'El equipo',
    role: 'Cofundador',
    footerNote: 'Un puente a Noruega — no un sustituto.',
  },
  ti: {
    nav: { hub: 'ሃብ', mission: 'ዕላማና', partners: 'ሽርክትና', contact: 'ርኸቡና', cta: 'ሃብ ዳህሲስካ' },
    badge: 'ንስደተኛታት ዝተሃነጸ፣ ብዝርዳኦም ሰባት',
    heroTitle: ['ኖርዌይ ሓዲሽ', 'ቤትካ እያ።'],
    heroEm: 'ወናናይ ኩና።',
    heroSub: 'ሓደ ቦታ። ነፍሲ ወከፍ መልሲ። ብቋንቋኻ። Global Integration ሃብ እዩ ኣብ ኖርዌይ ሓደሽቲ መጻእቲ ንዝምልከቶም ነገራት ብሩህ ዝኾነ፣ ዝተረጋገጸ ምርሻናት ዝረኽቡሉ — ካብ NAV ምምዝጋብ ክሳብ ፈለማ ማኪና ምግዛእ።',
    heroCta1: 'ናብ ሃብ ኺድ →',
    heroCta2: 'ዕላማና',
    searchLabel: 'ብዛዕባ ህይወት ኣብ ኖርዌይ ዝኾነ ሕቶ ሕተት',
    searchPlaceholder: 'ንኣብነት "ከመይ ጌረ ናይ ስራሕ-ኣልቦ ክፍሊት ይሓትት?"',
    searchEmpty: 'ዝኾነ ውጽኢት የለን — ካልእ ኣገላልጻ ፈትን።',
    searchViewAll: 'ምርሻ ርአ →',
    flagsLabel: 'ኣብ 8+ ቋንቋታት ይርከብ',
    stats: [{ number: '100+', label: 'ዝተሸፈኑ ርእሰ ነጥቢታት' }, { number: '8+', label: 'ዝድገፉ ቋንቋታት' }, { number: '0', label: 'ዋጋ ምጥቃም' }],
    hubLabel: 'ሃብ',
    hubTitle: 'ዘድልየካ ዅሉ፣ ኣብ ሓደ ቦታ።',
    hubSub: 'ብደረጃ-ብደረጃ ምርሻናት ንሓቀኛ ኩነታት። ዝኾነ ቴክኒካዊ ቃላት የለን። ዝኾነ ግምታት የለን። ምቹእ ሓበሬታ ጥራይ ምእንቲ ብምትእምማን ስጉምቲ ክትወስድ።',
    tagAvailable: 'ይርከብ',
    tagSoon: 'ዝኽእል ሽርክነት ይመጽእ ኣሎ',
    categories: [
      { icon: '🏛️', title: 'NAV', desc: 'ጥቕሚታት፣ ደገፍ ምድላይ ስራሕ፣ ኣልቦ ስራሕ — ብደረጃ-ብደረጃ።' },
      { icon: '💰', title: 'Skatteetaten', desc: 'ካርዳት ግብሪ፣ ምምላስ ግብሪ፣ D-ቁጽርታት ብሳሙና ዝተተርጎሙ።' },
      { icon: '🚗', title: 'Statens vegvesen', desc: 'ተሽከርካሪ ምምዝጋብ፣ ናይ ኖርዌጂያን ፍቓድ ምዝዋር ምርካብ።' },
      { icon: '📋', title: 'Folkeregisteret', desc: 'ኣድራሻኻ ምምዝጋብ፣ ሃገራዊ መለለዪ ቁጽሪ ምርካብ።' },
      { icon: '🏠', title: 'መንበሪ ቤት', desc: 'Finn.no ከመይ ከምዝሰርሕ፣ መሰላት ተኻራዪ፣ ቅድሚ ምፍራም ዝፍለጥ።' },
      { icon: '🏥', title: 'ሕክምና', desc: 'ኣብ ሓኪም ምምዝጋብ፣ ስርዓት fastlege ምርዳእ።' },
      { icon: '🏦', title: 'ባንካ', desc: 'ከም ሓደስቲ ናይ ኖርዌጂያን ባንካ ሕሳብ ምኽፋት።' },
      { icon: '🎓', title: 'ቋንቋ & ትምህርቲ', desc: 'ናይ ኖርዌጂያን ኮርሳት፣ norskprøve፣ ምምዝጋብ ቆልዑ ኣብ ቤት ትምህርቲ።' },
    ],
    missionLabel: 'ዕላማና',
    missionTitle: 'ድልድል፣ ምትካእ ዘይኮነ።',
    missionP1: 'ኖርዌይ ብሉጽ ህዝባዊ ኣገልግሎታት ኣለዋ። NAV፣ Skatteetaten፣ Statens vegvesen — ብጹሩይ ዝተሃነጹ ስርዓታት ዘሰርሑ። ግን ንሰባት ቀደም ኖርዌይ ከመይ ከምትሰርሕ ዝፈልጡ ዝተሃነጹ እዮም።',
    missionP2: 'Global Integration ናብኡ ዘብጽሓካ ድልድል እዩ። ኣሰካፊ ነገራት ብምብራህ ናብ ብሩህ ዝቅይሮ፣ ስለዚ ናብ NAV ምስ ኣትወካ ወይ Altinn ምስ ኣተዝካ፣ ቅድሚ ሕጂ ምን ከምዝጽበይ፣ ምን ከምትወስድ፣ ምን ከምትብል ትፈልጥ።',
    missionP3: 'ዝኾነ ናይ መንግስቲ ስርዓት ኣይትካእን። ንምጥቃሞም ንሕግዘካ — ብምትእምማን፣ ብናጽነት፣ ብናትካ ረቛሒታት።',
    value1Title: 'ብዓቕሚ ምብርባር ቅድሚ ዅሉ', value1Desc: 'ሓበሬታ ንህበካ። ንስካ ውሳነ ትወስድ።',
    value2Title: 'ብነጻ፣ ኩሉ ጊዜ', value2Desc: 'ናብ ሓበሬታ ምብጻሕ ናይ ውሑዳን ጥቕሚ ክኸውን የብሉን።',
    value3Title: 'ዝተረጋገጸ & ቅን', value3Desc: 'ዝነጸርናዮ ጥራይ ንዘርግሕ። ዝኾነ ግምት የለን።',
    bridgeYou: 'ንስካ', bridgeYouSub: 'ሓደስቲ ኣብ ኖርዌይ፣ ህይወትካ ሃኒጽካ ክትነብር ድልው',
    bridgeNorway: 'ኖርዌይ', bridgeNorwaySub: 'NAV · Skatteetaten · Vegvesen · Folkeregisteret',
    partnersLabel: 'ሽርክነታት',
    partnersTitle: 'ምስ ስርዓት ምስራሕ፣ ዘይኮነ ዙርያኡ።',
    partnersSub: 'ምስ ናይ ኖርዌጂያን ህዝባዊ ትካላት ሽርክነታት ብንጥፈት ሃኒጽና ኣለና ምእንቲ ምርሻናትና ዝተረጋገጸ፣ ዝሓደሰ፣ ናብ ቅኑዕ ቦታ ቀጥታ ዝቃላሕ ክኸውን። ካብ ሕጂ ምድሃን ኣይህሉን።',
    partnerStatus: 'ኣብ ፓይፕላይን',
    partnerNote: 'ካብ ሓደ ካብዞም ትካላት ኢኻ፣ ክትተሓባበር ትደሊ?',
    partnerNoteCta: 'ርኸቡና →',
    footerTagline: 'ናብ ህይወት ኣብ ኖርዌይ ዝወስደካ ድልድልካ።\nብሩህ ምርሻናት። ዝተረጋገጸ ሓበሬታ። ብነጻ፣ ኩሉ ጊዜ።',
    teamLabel: 'ጋንታ',
    role: 'ሓባራዊ መስረቲ',
    footerNote: 'ናብ ኖርዌይ ድልድል — ምትካእ ዘይኮነ።',
  },
};

// NAV is now "live" — the Dagpenger guide is built.
const categoryTags = [
  'tagAvailable',
  'tagSoon', 'tagSoon', 'tagSoon',
  'tagAvailable', 'tagAvailable', 'tagAvailable', 'tagAvailable',
];

// Map each hub card to a route, or null if the guide isn't built yet.
const categoryRoutes = [
  '/guider/nav/dagpenger', null, null, null,
  null, null, null, null,
];

// Search index — keywords cover common phrasings across the supported languages
// so a substring match surfaces the guide regardless of how the user types.
export const searchIndex = [
  {
    path: '/guider/nav/dagpenger',
    category: 'NAV',
    titles: {
      no: 'Dagpenger — når du har mistet jobben',
      en: 'Unemployment benefits — when you\'ve lost your job',
    },
    summaries: {
      no: 'Steg-for-steg-guide: fra første dag uten jobb til første utbetaling.',
      en: 'Step-by-step: from your first day without work to your first payment.',
    },
    keywords: [
      'dagpenger', 'mistet jobb', 'mistet jobben', 'arbeidsledig', 'arbeidsledighet',
      'ledig', 'sagt opp', 'oppsigelse', 'permittert', 'permittering',
      'unemployment', 'unemployment benefits', 'lost job', 'lost my job',
      'jobless', 'out of work', 'laid off', 'fired', 'benefit', 'benefits',
      'zasiłek', 'bezrobocie', 'straciłem pracę', 'bezrobotny',
      'desempleo', 'paro', 'subsidio', 'perdí el trabajo',
      'بطالة', 'إعانة', 'فقدت وظيفتي',
      'shaqo la\'aan', 'lacag shaqo la\'aan',
      'بے روزگاری', 'نوکری چلی گئی',
      'ኣልቦ ስራሕ', 'ዝተሰናበተ',
      'nav',
    ],
  },
];

function LandingPage({ lang, setLang, menuOpen, setMenuOpen }) {
  const t = translations[lang];
  const navigate = useNavigate();

  const languages = [
    { code: '1f1ec-1f1e7', label: 'English',   key: 'en' },
    { code: '1f1f8-1f1e6', label: 'Arabic',    key: 'ar' },
    { code: '1f1f8-1f1f4', label: 'Somali',    key: 'so' },
    { code: '1f1f5-1f1f1', label: 'Polish',    key: 'pl' },
    { code: '1f1ea-1f1f7', label: 'Tigrinya',  key: 'ti' },
    { code: '1f1f5-1f1f0', label: 'Urdu',      key: 'ur' },
    { code: '1f1ea-1f1f8', label: 'Spanish',   key: 'es' },
    { code: '1f1f3-1f1f4', label: 'Norwegian', key: 'no' },
  ];

  const twemojiUrl = (code) =>
    `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${code}.svg`;

  const rtlLangs = ['ar', 'ur'];
  const dir = rtlLangs.includes(lang) ? 'rtl' : 'ltr';

  // ── Search state ──────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const searchBoxRef = useRef(null);

  const q = searchQuery.trim().toLowerCase();
  const searchResults = q.length >= 2
    ? searchIndex
        .map((item) => {
          const hitCount = item.keywords.filter((k) =>
            k.toLowerCase().includes(q) || q.includes(k.toLowerCase())
          ).length;
          return hitCount > 0 ? { ...item, hitCount } : null;
        })
        .filter(Boolean)
        .sort((a, b) => b.hitCount - a.hitCount)
    : [];

  useEffect(() => {
    const onClick = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      navigate(searchResults[0].path);
    }
  };

  const quickTags = [
    { label: 'NAV', prefill: 'nav' },
    { label: lang === 'en' ? 'Unemployment' : 'Dagpenger', prefill: 'dagpenger' },
    { label: 'D-number', prefill: 'd-number' },
    { label: 'Finn.no', prefill: 'finn' },
    { label: 'Fastlege', prefill: 'fastlege' },
  ];

  const showDropdown = searchFocused && q.length >= 2;

  return (
    <div className="page" dir={dir}>

      {/* HEADER */}
      <header className="header">
        <Link to="/"><img src="GI.png" alt="Global Integration logo" className="logo" /></Link>
        <nav className="nav">
          <a href="#hub">{t.nav.hub}</a>
          <a href="#mission">{t.nav.mission}</a>
          <a href="#partners">{t.nav.partners}</a>
          <a href="#contact">{t.nav.contact}</a>
        </nav>
        <a href="#hub" className="nav-cta">{t.nav.cta}</a>
        <button
          className={`hamburger ${menuOpen ? 'hamburger-open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </header>
      {menuOpen && (
        <div className="mobile-nav" onClick={() => setMenuOpen(false)}>
          <a href="#hub">{t.nav.hub}</a>
          <a href="#mission">{t.nav.mission}</a>
          <a href="#partners">{t.nav.partners}</a>
          <a href="#contact">{t.nav.contact}</a>
          <a href="#hub" className="mobile-nav-cta">{t.nav.cta}</a>
        </div>
      )}

      {/* HERO */}
      <section className="hero">
        <div className="hero-texture"></div>
        <div className="hero-inner">
          <div className="hero-left">
            <div className="badge">
              <span className="badge-dot"></span>
              {t.badge}
            </div>
            <h1 className="hero-title">
              {t.heroTitle[0]}<br />
              {t.heroTitle[1]}<br />
              <em>{t.heroEm}</em>
            </h1>
            <p className="hero-sub">{t.heroSub}</p>
            <div className="hero-cta">
              <a href="#hub" className="btn-primary">{t.heroCta1}</a>
              <a href="#mission" className="btn-ghost">{t.heroCta2}</a>
            </div>
          </div>

          <div className="hero-right">
            <form className="search-card" onSubmit={handleSearchSubmit} ref={searchBoxRef}>
              <div className="search-card-label">{t.searchLabel}</div>
              <div className={`search-box ${searchFocused ? 'search-box-focused' : ''}`}>
                <span className="search-icon" aria-hidden>🔍</span>
                <input
                  type="text"
                  className="search-input"
                  placeholder={t.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  aria-label={t.searchLabel}
                />
              </div>

              {showDropdown && (
                <div className="search-dropdown">
                  {searchResults.length === 0 ? (
                    <div className="search-empty">{t.searchEmpty}</div>
                  ) : (
                    searchResults.map((r) => (
                      <Link
                        to={r.path}
                        key={r.path}
                        className="search-result"
                        onClick={() => setSearchFocused(false)}
                      >
                        <div className="search-result-category">{r.category}</div>
                        <div className="search-result-title">
                          {r.titles[lang] || r.titles.en}
                        </div>
                        <div className="search-result-summary">
                          {r.summaries[lang] || r.summaries.en}
                        </div>
                        <span className="search-result-cta">{t.searchViewAll}</span>
                      </Link>
                    ))
                  )}
                </div>
              )}

              <div className="search-tags">
                {quickTags.map((tag) => (
                  <button
                    type="button"
                    key={tag.label}
                    className="tag"
                    onClick={() => {
                      setSearchQuery(tag.prefill);
                      setSearchFocused(true);
                    }}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </form>
            <div className="flags-row">
              {languages.map((l) => (
                <button
                  key={l.key}
                  className={`flag-btn${lang === l.key ? ' flag-btn-active' : ''}`}
                  onClick={() => setLang(l.key)}
                  title={l.label}
                  aria-label={`Switch to ${l.label}`}
                >
                  <img
                    src={twemojiUrl(l.code)}
                    alt={l.label}
                    className="flag-img"
                  />
                </button>
              ))}
            </div>
            <p className="flags-label">{t.flagsLabel}</p>
          </div>
        </div>

        <div className="stats-bar">
          {t.stats.map((s, i) => (
            <div className="stat" key={i}>
              <span className="stat-num">{s.number}</span>
              <span className="stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* HUB GRID */}
      <section className="hub" id="hub">
        <div className="section-inner">
          <div className="section-label">{t.hubLabel}</div>
          <h2>{t.hubTitle}</h2>
          <p className="section-sub">{t.hubSub}</p>
          <div className="hub-grid">
            {t.categories.map((cat, i) => {
              const tagKey = categoryTags[i];
              const tagLabel = t[tagKey];
              const isLive = tagKey === 'tagAvailable';
              const route = categoryRoutes[i];
              const clickable = route != null;

              const inner = (
                <>
                  <div className="hub-card-top">
                    <span className="hub-icon">{cat.icon}</span>
                    <span className={`hub-tag ${isLive ? 'tag-live' : 'tag-soon'}`}>
                      {tagLabel}
                    </span>
                  </div>
                  <h3>{cat.title}</h3>
                  <p>{cat.desc}</p>
                  {clickable && <span className="hub-arrow">→</span>}
                </>
              );

              return clickable ? (
                <Link to={route} className="hub-card hub-card-live hub-card-link" key={i}>
                  {inner}
                </Link>
              ) : (
                <div
                  className={`hub-card ${isLive ? 'hub-card-live' : 'hub-card-soon'}`}
                  key={i}
                >
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="mission" id="mission">
        <div className="mission-inner">
          <div className="mission-text">
            <div className="section-label">{t.missionLabel}</div>
            <h2>{t.missionTitle}</h2>
            <p>{t.missionP1}</p>
            <p>{t.missionP2}</p>
            <p>{t.missionP3}</p>
            <div className="mission-values">
              <div className="value">
                <span className="value-icon">💪</span>
                <div>
                  <strong>{t.value1Title}</strong>
                  <p>{t.value1Desc}</p>
                </div>
              </div>
              <div className="value">
                <span className="value-icon">🔓</span>
                <div>
                  <strong>{t.value2Title}</strong>
                  <p>{t.value2Desc}</p>
                </div>
              </div>
              <div className="value">
                <span className="value-icon">✅</span>
                <div>
                  <strong>{t.value3Title}</strong>
                  <p>{t.value3Desc}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mission-visual">
            <div className="bridge-card">
              <div className="bridge-side bridge-you">
                <span>{t.bridgeYou}</span>
                <p>{t.bridgeYouSub}</p>
              </div>
              <div className="bridge-middle">
                <div className="bridge-line"></div>
                <div className="bridge-logo-wrap">
                  <img src="GI.png" alt="Global Integration" className="bridge-logo" />
                </div>
                <div className="bridge-line"></div>
              </div>
              <div className="bridge-side bridge-system">
                <span>{t.bridgeNorway}</span>
                <p>{t.bridgeNorwaySub}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="partners" id="partners">
        <div className="section-inner">
          <div className="section-label">{t.partnersLabel}</div>
          <h2>{t.partnersTitle}</h2>
          <p className="section-sub">{t.partnersSub}</p>
          <div className="partner-grid">
            {['NAV', 'Skatteetaten', 'Statens vegvesen', 'Folkeregisteret', 'Husbanken', 'UDI'].map((p, i) => (
              <div className="partner-chip" key={i}>
                <span className="partner-dot"></span>
                {p}
                <span className="partner-status">{t.partnerStatus}</span>
              </div>
            ))}
          </div>
          <p className="partner-note">
            {t.partnerNote}{' '}
            <a href="#contact">{t.partnerNoteCta}</a>
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer" id="contact">
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <img src="GI.png" alt="Global Integration logo" className="footer-logo" />
              <p className="footer-tagline">
                {t.footerTagline.split('\n').map((line, i) => (
                  <span key={i}>{line}{i === 0 && <br />}</span>
                ))}
              </p>
              <a href="mailto:globalintegration.as@gmail.com" className="footer-email">
                globalintegration.as@gmail.com
              </a>
            </div>

            <div className="footer-team">
              <div className="footer-section-label">{t.teamLabel}</div>
              <div className="footer-people">
                <div className="footer-person">
                  <img src="benjamin_linkedin.jpg" alt="Benjamin Sundell Frivoll" className="footer-avatar" />
                  <div className="footer-person-info">
                    <strong>Benjamin Sundell Frivoll</strong>
                    <span className="footer-role">{t.role}</span>
                    <div className="footer-links">
                      <a href="mailto:benjamin.frivoll@icloud.com">✉ benjamin.frivoll@icloud.com</a>
                      <a href="https://www.linkedin.com/in/benjamin-frivoll-0855b332a" target="_blank" rel="noopener noreferrer" className="footer-li">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        LinkedIn
                      </a>
                    </div>
                  </div>
                </div>

                <div className="footer-person">
                  <img src="oscar_linkedin.jpg" alt="Oscar Børud Mosæther" className="footer-avatar" />
                  <div className="footer-person-info">
                    <strong>Oscar Børud Mosæther</strong>
                    <span className="footer-role">{t.role}</span>
                    <div className="footer-links">
                      <a href="mailto:oscarbmosaether@gmail.com">✉ oscarbmosaether@gmail.com</a>
                      <a href="https://www.linkedin.com/in/oscar-børud-mosæther-5b15b232a" target="_blank" rel="noopener noreferrer" className="footer-li">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        LinkedIn
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-bottom-bar">
            <p>© {new Date().getFullYear()} Global Integration. All rights reserved.</p>
            <p className="footer-bottom-note">{t.footerNote}</p>
          </div>
        </div>
      </footer>

    </div>
  );
}

// ─── App: router wrapper ─────────────────────────────────────────────
function App() {
  const [lang, setLang] = useState('en');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 960) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const commonProps = { lang, setLang, menuOpen, setMenuOpen };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage {...commonProps} />} />
        <Route path="/guider/nav/dagpenger" element={<DagpengerGuide {...commonProps} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;